import { DEFAULT_PAGE_SIZE } from '@w7/ui'
import { apiFetch } from '@/api/client'
import type {
  BlockedIpPlayer,
  BlockedIpQuery,
  BlockedIpResult,
  BlockedIpRow,
} from '@/features/logs/blockedIpTypes'
import { deleteBlockedIp, type BlockedIpWriteResponse } from './blockedDevices'

export type { BlockedIpWriteResponse }

/** Agent-API `POST /v1/blockedIp/getBlockedIps` request. */
export type GetBlockedIpsRequest = {
  page?: number
  pageSize?: number
  ip?: string
  player?: string
}

export type BlockedIpApiPlayer = {
  id?: string
  _id?: string
  username?: string
  accessLevel?: string
  siteUrl?: string
}

/** One Lab/Agent-API blocked-IP row (Winner + Backend-UI fields). */
export type BlockedIpApiRow = {
  id?: string
  _id?: string
  IP?: string
  ip?: string
  comment?: string
  createdAt?: string
  playerCount?: number
  players?: BlockedIpApiPlayer[]
  playerList?: Array<{ _id?: string }>
  user?: BlockedIpApiPlayer[]
}

/** Lab/Winner callback envelope from hierarchy-service. */
export type GetBlockedIpsResponse = {
  value: boolean
  data: {
    dataArray?: BlockedIpApiRow[]
    results?: BlockedIpApiRow[]
    totalCount?: number
    total?: number
    totalItems?: number
    limit?: number
    count?: number
    maxRow?: number
    noOfPages?: number
    page?: number
  }
}

/**
 * Winner USER-V2 `BlockedIp/getBlockedIps` → Agent-API hierarchy.
 */
export async function getBlockedIps(
  body: GetBlockedIpsRequest = {},
): Promise<GetBlockedIpsResponse> {
  const out: Record<string, unknown> = {
    page: body.page ?? 1,
    pageSize: body.pageSize ?? DEFAULT_PAGE_SIZE,
  }
  const ip = body.ip?.trim()
  const player = body.player?.trim()
  if (ip) out.ip = ip
  if (player) out.player = player

  return apiFetch<GetBlockedIpsResponse>('/v1/blockedIp/getBlockedIps', {
    method: 'POST',
    body: JSON.stringify(out),
  })
}

/**
 * Winner USER-V2 `BlockedIp/save` → Agent-API hierarchy.
 * Body: `{ IP, comment? }` for Logs → Blocked IP.
 */
export async function saveBlockedIpAddress(body: {
  IP: string
  comment?: string
}): Promise<BlockedIpWriteResponse> {
  const out: Record<string, unknown> = {
    IP: body.IP.trim(),
  }
  if (body.comment != null) out.comment = body.comment
  return apiFetch<BlockedIpWriteResponse>('/v1/blockedIp/save', {
    method: 'POST',
    body: JSON.stringify(out),
  })
}

/** Re-export shared delete (IP or device row uuid). */
export { deleteBlockedIp }

/** Map hierarchy rows → console Blocked IP table. */
export function toBlockedIpResult(
  live: GetBlockedIpsResponse,
  query: BlockedIpQuery = {},
): BlockedIpResult {
  const pageSize =
    live.data?.limit ?? live.data?.count ?? live.data?.maxRow ?? query.pageSize ?? DEFAULT_PAGE_SIZE
  const page = live.data?.page ?? query.page ?? 1
  const total =
    live.data?.totalCount ?? live.data?.total ?? live.data?.totalItems ?? 0
  const raw = live.data?.dataArray ?? live.data?.results ?? []
  const rows: BlockedIpRow[] = raw.map(mapRow)
  return { rows, total, page, pageSize }
}

function mapRow(row: BlockedIpApiRow): BlockedIpRow {
  const playersRaw = row.players ?? row.user ?? []
  const players: BlockedIpPlayer[] = []
  for (const p of playersRaw) {
    const mapped = mapPlayer(p)
    if (mapped) players.push(mapped)
  }
  if (players.length === 0 && row.playerList?.length) {
    for (const p of row.playerList) {
      const username = String(p._id ?? '').trim()
      if (!username) continue
      players.push({ id: username, username })
    }
  }

  return {
    id: String(row.id ?? row._id ?? ''),
    ip: String(row.IP ?? row.ip ?? ''),
    comment: String(row.comment ?? ''),
    createdAt: String(row.createdAt ?? ''),
    players,
  }
}

function mapPlayer(p: BlockedIpApiPlayer | undefined): BlockedIpPlayer | null {
  const username = String(p?.username ?? p?._id ?? '').trim()
  if (!username) return null
  const id = String(p?.id ?? p?._id ?? username).trim() || username
  return { id, username }
}
